/**
 * src/mcp/components/MCPMessage.jsx
 *
 * Relocated from src/components/MCPMessage.jsx — grouped with the rest of the
 * MCP chat feature (folder-structure cleanup) since this file has no
 * consumers outside mcp/pages/MCPChat.jsx. Content unchanged.
 *
 * Chat message component with modern UI
 */

export default function MCPMessage({ message }) {
  const isUser = message.role === 'user';
  const isError = message.isError;
  const isStreaming = message.isStreaming;

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatContent = (content) => {
    if (!content) return null;

    // Simple code block detection
    const parts = content.split(/(```[\s\S]*?```)/g);

    return parts.map((part, i) => {
      if (part.startsWith('```')) {
        const match = part.match(/```(\w*)\n?([\s\S]*?)```/);
        const lang = match?.[1] || '';
        const code = match?.[2] || part.replace(/```\w*\n?/, '').replace(/```$/, '');
        return (
          <div key={i} className="mcp-code-block">
            {lang && <div className="mcp-code-lang">{lang}</div>}
            <pre><code>{code}</code></pre>
          </div>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div className={`mcp-message ${isUser ? 'mcp-message-user' : 'mcp-message-assistant'} ${isError ? 'mcp-message-error' : ''}`}>
      <div className="mcp-message-avatar">
        {isUser ? '👤' : '🤖'}
      </div>

      <div className="mcp-message-body">
        <div className="mcp-message-header">
          <span className="mcp-message-sender">{isUser ? 'You' : 'Agent'}</span>
          <span className="mcp-message-time">{formatTime(message.timestamp)}</span>
        </div>

        <div className="mcp-message-content">
          {isStreaming && !message.content ? (
            <span className="mcp-cursor">▊</span>
          ) : (
            formatContent(message.content)
          )}
        </div>

        {/* Tool Calls */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className="mcp-tool-calls">
            <div className="mcp-tool-calls-header">
              <span className="mcp-tool-icon">⚡</span>
              <span>Tools Used ({message.toolCalls.length})</span>
            </div>
            <div className="mcp-tool-calls-list">
              {message.toolCalls.map((call, i) => (
                <div key={i} className="mcp-tool-call">
                  <div className="mcp-tool-call-header">
                    <span className="mcp-tool-call-name">{call.tool}</span>
                    {call.error ? (
                      <span className="mcp-tool-status error">Failed</span>
                    ) : (
                      <span className="mcp-tool-status success">Success</span>
                    )}
                  </div>
                  {call.arguments && Object.keys(call.arguments).length > 0 && (
                    <div className="mcp-tool-call-args">
                      <span className="mcp-args-label">Args:</span>
                      <code>{JSON.stringify(call.arguments)}</code>
                    </div>
                  )}
                  {call.result && (
                    <div className="mcp-tool-call-result">
                      <span className="mcp-result-label">Result:</span>
                      <pre>{typeof call.result === 'string' ? call.result : JSON.stringify(call.result, null, 2)}</pre>
                    </div>
                  )}
                  {call.error && (
                    <div className="mcp-tool-call-error">
                      <span className="mcp-error-label">Error:</span>
                      <span>{call.error}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .mcp-message {
          display: flex;
          gap: 14px;
          margin-bottom: 24px;
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .mcp-message-user {
          flex-direction: row-reverse;
        }

        .mcp-message-avatar {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
        }

        .mcp-message-user .mcp-message-avatar {
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          border: none;
        }

        .mcp-message-assistant .mcp-message-avatar {
          background: linear-gradient(135deg, #10b981, #059669);
          border: none;
        }

        .mcp-message-body {
          flex: 1;
          min-width: 0;
          max-width: 85%;
        }

        .mcp-message-user .mcp-message-body {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
        }

        .mcp-message-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 6px;
        }

        .mcp-message-user .mcp-message-header {
          flex-direction: row-reverse;
        }

        .mcp-message-sender {
          font-size: 13px;
          font-weight: 600;
          color: #e4e4e7;
        }

        .mcp-message-time {
          font-size: 11px;
          color: #52525b;
          font-family: 'JetBrains Mono', monospace;
        }

        .mcp-message-content {
          font-size: 14px;
          line-height: 1.7;
          color: #a1a1aa;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .mcp-message-user .mcp-message-content {
          background: rgba(59, 130, 246, 0.15);
          border: 1px solid rgba(59, 130, 246, 0.2);
          color: #e4e4e7;
          padding: 12px 16px;
          border-radius: 16px 16px 4px 16px;
        }

        .mcp-message-assistant .mcp-message-content {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          padding: 14px 18px;
          border-radius: 16px 16px 16px 4px;
        }

        .mcp-message-error .mcp-message-content {
          background: rgba(239, 68, 68, 0.1);
          border-color: rgba(239, 68, 68, 0.2);
          color: #fca5a5;
        }

        .mcp-cursor {
          color: #10b981;
          animation: blink 1s infinite;
        }

        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }

        /* Code Blocks */
        .mcp-code-block {
          background: #0a0a0b;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 10px;
          margin: 12px 0;
          overflow: hidden;
        }

        .mcp-code-lang {
          background: rgba(255,255,255,0.05);
          padding: 6px 12px;
          font-size: 11px;
          color: #71717a;
          font-family: 'JetBrains Mono', monospace;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }

        .mcp-code-block pre {
          margin: 0;
          padding: 14px;
          overflow-x: auto;
        }

        .mcp-code-block code {
          font-family: 'JetBrains Mono', 'Fira Code', monospace;
          font-size: 12px;
          color: #e4e4e7;
          line-height: 1.6;
        }

        /* Tool Calls */
        .mcp-tool-calls {
          margin-top: 14px;
          background: rgba(245, 158, 11, 0.05);
          border: 1px solid rgba(245, 158, 11, 0.15);
          border-radius: 12px;
          overflow: hidden;
        }

        .mcp-tool-calls-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          background: rgba(245, 158, 11, 0.1);
          font-size: 12px;
          font-weight: 600;
          color: #f59e0b;
          border-bottom: 1px solid rgba(245, 158, 11, 0.15);
        }

        .mcp-tool-icon {
          font-size: 14px;
        }

        .mcp-tool-calls-list {
          padding: 8px;
        }

        .mcp-tool-call {
          background: rgba(0,0,0,0.2);
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 8px;
        }

        .mcp-tool-call:last-child {
          margin-bottom: 0;
        }

        .mcp-tool-call-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .mcp-tool-call-name {
          font-size: 12px;
          font-weight: 600;
          color: #10b981;
          font-family: 'JetBrains Mono', monospace;
        }

        .mcp-tool-status {
          font-size: 10px;
          padding: 3px 8px;
          border-radius: 10px;
          font-weight: 600;
        }

        .mcp-tool-status.success {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
        }

        .mcp-tool-status.error {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .mcp-tool-call-args,
        .mcp-tool-call-result,
        .mcp-tool-call-error {
          margin-top: 8px;
        }

        .mcp-args-label,
        .mcp-result-label,
        .mcp-error-label {
          font-size: 10px;
          color: #71717a;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          display: block;
          margin-bottom: 4px;
        }

        .mcp-tool-call-args code {
          font-size: 11px;
          font-family: 'JetBrains Mono', monospace;
          color: #a1a1aa;
          word-break: break-all;
        }

        .mcp-tool-call-result pre {
          margin: 0;
          font-size: 11px;
          font-family: 'JetBrains Mono', monospace;
          color: #a1a1aa;
          white-space: pre-wrap;
          word-break: break-all;
          max-height: 150px;
          overflow-y: auto;
          background: rgba(0,0,0,0.3);
          padding: 8px;
          border-radius: 6px;
        }

        .mcp-tool-call-error {
          color: #fca5a5;
          font-size: 12px;
        }
      `}</style>
    </div>
  );
}
